import { authHelper } from '../utils/authHelper';
import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  Settings, 
  Layout, 
  Shield,
  TrendingUp,
  DollarSign,
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { AdminLandingEditor } from './AdminLandingEditor';
import { AdminSubscriptions } from './AdminSubscriptions';
import { AdminPanel as AdminUsersPanel } from './AdminPanel';
import { AdminConfigPanel } from './AdminConfigPanel';
import { AdminOverview } from './AdminOverview';
import { AdminManagers } from './AdminManagers';

export function ControlPanel() {
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubscriptions: 0,
    revenue: 0,
    trialUsers: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    // Fetch general stats
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${authHelper.getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const sections = [
    { 
      id: 'overview', 
      name: 'Panel General', 
      icon: LayoutDashboard,
      description: 'Resumen y métricas principales'
    },
    { 
      id: 'landing', 
      name: 'Landing Page', 
      icon: Layout,
      description: 'Editar contenido y precios'
    },
    { 
      id: 'users', 
      name: 'Usuarios', 
      icon: Users,
      description: 'Gestionar usuarios y permisos'
    },
    { 
      id: 'subscriptions', 
      name: 'Suscripciones', 
      icon: CreditCard,
      description: 'Ver pagos y estados'
    },
    { 
      id: 'admins', 
      name: 'Administradores', 
      icon: Shield,
      description: 'Gestionar acceso admin'
    },
    { 
      id: 'config', 
      name: 'Configuración', 
      icon: Settings,
      description: 'Stripe, emails y ajustes'
    }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return <AdminOverview stats={stats} />;
      case 'landing':
        return <AdminLandingEditor />;
      case 'users':
        return <AdminUsersPanel />;
      case 'subscriptions':
        return <AdminSubscriptions />;
      case 'admins':
        return <AdminManagers />;
      case 'config':
        return <AdminConfigPanel />;
      default:
        return <AdminOverview stats={stats} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#02040A]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#02040A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#C5A14C] flex items-center justify-center">
                  <LayoutDashboard className="w-7 h-7 text-[#02040A]" />
                </div>
                Panel de Control
              </h1>
              <p className="text-white/60 mt-2">Gestión completa de MÉTODO 11</p>
            </div>

            {/* Quick Stats */}
            <div className="flex gap-4">
              <div className="bg-white/5 backdrop-blur-md rounded-xl px-4 py-3 border border-white/10">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-white/60" />
                  <div>
                    <p className="text-xs text-white/60">Usuarios</p>
                    <p className="text-xl font-bold text-white">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-md rounded-xl px-4 py-3 border border-white/10">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#C5A14C]" />
                  <div>
                    <p className="text-xs text-white/60">Ingresos</p>
                    <p className="text-xl font-bold text-[#C5A14C]">${stats.revenue}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/5 backdrop-blur-md rounded-xl px-4 py-3 border border-white/10">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-emerald-500" />
                  <div>
                    <p className="text-xs text-white/60">Activos</p>
                    <p className="text-xl font-bold text-emerald-500">{stats.activeSubscriptions}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar Navigation */}
          <div className="col-span-3">
            <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-4 sticky top-6">
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all duration-300 ${
                        activeSection === section.id
                          ? 'bg-[#C5A14C] text-[#02040A] shadow-lg'
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <div>
                          <p className="font-semibold">{section.name}</p>
                          <p className={`text-xs ${activeSection === section.id ? 'text-[#02040A]/70' : 'text-white/50'}`}>
                            {section.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </nav>

              {/* System Status */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center gap-2 text-emerald-500">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-sm font-semibold">Sistema Operativo</span>
                </div>
                <p className="text-xs text-white/40 mt-1">Todos los servicios funcionando</p>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-6">
              {renderSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

